import styles from "./Spark.module.css";

/** Bar height in px for a rating: 1 → 10px, 10 → 28px (design/Phase-3-Screens.dc.html, `spark`). */
export const sparkHeight = (rating: number) => 8 + rating * 2;

/** The unrated bar sits at the baseline minimum, so a gap in the album is visible as a gap. */
export const UNRATED_HEIGHT = 8;

export interface SparkProps {
  /** One entry per core track, in track order. `null` is unrated — never a zero-height bar. */
  ratings: readonly (number | null)[];
  /** Column width in px. Bars shrink to fit rather than being silently clipped. */
  width: number;
  label?: string;
}

const BAR_MAX = 5;
const GAPS = [2, 1, 0];

/**
 * Every core track's rating as one bar, in track order — the shape of the album beside its score.
 *
 * The design draws 5px bars with a 2px gap, which fits the twelve-track albums in the mocks. A
 * 24-track double album does not fit, so the bars narrow until it does. Clipping instead would
 * make a long record look like a short one, and the whole point of the column is the shape.
 */
export function Spark({ ratings, width, label }: SparkProps) {
  const n = ratings.length;
  if (n === 0) return <div className={styles.spark} style={{ width }} />;

  let gap = GAPS[0]!;
  let bar = 0;
  for (const candidate of GAPS) {
    bar = Math.floor((width - candidate * (n - 1)) / n);
    if (bar >= 1) {
      gap = candidate;
      break;
    }
  }
  bar = Math.min(BAR_MAX, Math.max(1, bar));

  return (
    <div
      className={styles.spark}
      style={{ width, gap: `${gap}px` }}
      role="img"
      aria-label={label ?? `${ratings.filter((r) => r !== null).length} of ${n} core tracks rated`}
    >
      {ratings.map((rating, i) => (
        <div
          // Bars are positional: there is no id, and two tracks can share a rating.
          key={i}
          className={styles.bar}
          data-unrated={rating === null || undefined}
          style={{ width: bar, height: rating === null ? UNRATED_HEIGHT : sparkHeight(rating) }}
        />
      ))}
    </div>
  );
}
